import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const STORAGE_KEY = 'cyberpunk-os-windows-v5';

/**
 * Janela padrão. Posições iniciais ajustadas para dentro da área visível da tela.
 */
const defaultWindow = (overrides = {}) => ({
  x: 60,
  y: 40,
  w: 720,
  h: 440,
  z: 10,
  isOpen: false,
  isMinimized: false,
  isMaximized: false,
  pinned: false,
  ...overrides,
});

/**
 * Registro inicial das janelas do desktop OS.
 */
const SEED_WINDOWS = {
  about:       { ...defaultWindow({ x: 110, y: 40, w: 720, h: 440 }), isOpen: false, title: 'About',         tone: 'cyan',   pinned: false, icon: 'user' },
  projects:    { ...defaultWindow({ x: 130, y: 50, w: 820, h: 460 }), isOpen: false, title: 'Work',          tone: 'violet', pinned: false, icon: 'code' },
  resume:      { ...defaultWindow({ x: 180, y: 55, w: 560, h: 360 }), isOpen: false, title: 'Resume.pdf',    tone: 'cyan',   pinned: false, icon: 'file-text' },
  skills:      { ...defaultWindow({ x: 150, y: 60, w: 720, h: 420 }), isOpen: false, title: 'skills.exe',    tone: 'violet', pinned: false, icon: 'cpu' },
  experience:  { ...defaultWindow({ x: 170, y: 70, w: 760, h: 440 }), isOpen: false, title: 'experience.exe',tone: 'cyan',   pinned: false, icon: 'briefcase' },
  certificates:{ ...defaultWindow({ x: 190, y: 60, w: 720, h: 420 }), isOpen: false, title: 'Awards',        tone: 'green',  pinned: false, icon: 'award' },
  contact:     { ...defaultWindow({ x: 210, y: 70, w: 620, h: 400 }), isOpen: false, title: 'Contact',       tone: 'pink',   pinned: false, icon: 'mail' },
  trash:       { ...defaultWindow({ x: 250, y: 80, w: 420, h: 300 }), isOpen: false, title: 'Trash',         tone: 'green',  pinned: false, icon: 'trash' },
  terminal:    { ...defaultWindow({ x: 230, y: 50, w: 660, h: 400 }), isOpen: false, title: 'terminal.tty',  tone: 'green',  pinned: false, icon: 'terminal' },
  lofi:        { ...defaultWindow({ x: 680, y: 40, w: 340, h: 180 }), isOpen: false, title: 'lofi.fm',       tone: 'violet', pinned: false, icon: 'music' },
};

let topZ = 10;

export const useWindowsStore = create(
  persist(
    (set, get) => ({
      windows: SEED_WINDOWS,
      topZ,

      openWindow: (id) => set((s) => {
        const w = s.windows[id];
        if (!w) return s;
        return {
          windows: {
            ...s.windows,
            [id]: { ...w, isOpen: true, isMinimized: false, z: s.topZ + 1 },
          },
          topZ: s.topZ + 1,
        };
      }),

      closeWindow: (id) => set((s) => {
        const w = s.windows[id];
        if (!w) return s;
        return {
          windows: { ...s.windows, [id]: { ...w, isOpen: false } },
        };
      }),

      focusWindow: (id) => set((s) => {
        const w = s.windows[id];
        if (!w || !w.isOpen) return s;
        if (w.z === s.topZ) return s; // already on top — no-op
        return {
          windows: {
            ...s.windows,
            [id]: { ...w, z: s.topZ + 1 },
          },
          topZ: s.topZ + 1,
        };
      }),

      minimizeWindow: (id) => set((s) => {
        const w = s.windows[id];
        if (!w || w.pinned) return s;
        return {
          windows: { ...s.windows, [id]: { ...w, isMinimized: true } },
        };
      }),

      restoreWindow: (id) => set((s) => {
        const w = s.windows[id];
        if (!w) return s;
        return {
          windows: {
            ...s.windows,
            [id]: { ...w, isMinimized: false, z: s.topZ + 1 },
          },
          topZ: s.topZ + 1,
        };
      }),

      toggleMaximize: (id) => set((s) => {
        const w = s.windows[id];
        if (!w) return s;
        return {
          windows: {
            ...s.windows,
            [id]: { ...w, isMaximized: !w.isMaximized },
          },
        };
      }),

      moveWindow: (id, { x, y }) => set((s) => {
        const w = s.windows[id];
        if (!w || w.isMaximized) return s;
        return {
          windows: { ...s.windows, [id]: { ...w, x, y } },
        };
      }),

      resizeWindow: (id, payload) => set((s) => {
        const windowItem = s.windows[id];
        if (!windowItem || windowItem.isMaximized) return s;
        const targetW = payload?.w ?? payload?.width ?? windowItem.w;
        const targetH = payload?.h ?? payload?.height ?? windowItem.h;
        return {
          windows: { ...s.windows, [id]: { ...windowItem, w: targetW, h: targetH } },
        };
      }),

      toggleWindow: (id) => {
        const s = get();
        const w = s.windows[id];
        if (!w) return;
        if (!w.isOpen || w.isMinimized) {
          get().openWindow(id);
        } else if (w.z === s.topZ) {
          get().minimizeWindow(id);
        } else {
          get().focusWindow(id);
        }
      },

      resetLayout: () => set({ windows: SEED_WINDOWS, topZ: 10 }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ windows: s.windows, topZ: s.topZ }),
      skipHydration: true, // hidrata manualmente em <App /> — evita loop de rehydrate
    }
  )
);

/**
 * Hook de hidratação. Chamar 1x no App.
 * Documentado em https://github.com/pmndrs/zustand/issues/1149
 */
export function hydrateWindowsStore() {
  if (typeof window === 'undefined') return;
  // Reset transient state on rehydrate — isOpen/isMinimized devem voltar ao seed
  useWindowsStore.persist.rehydrate();
  const current = useWindowsStore.getState().windows;
  const missing = Object.fromEntries(
    Object.entries(SEED_WINDOWS).filter(([id]) => !current[id])
  );
  if (Object.keys(missing).length) {
    useWindowsStore.setState({ windows: { ...current, ...missing } });
  }
}
